import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DataTable, type DataTableColumn } from "@tubo/ui/components/ui/data-table";
import {
  useSecondaryNavActions,
  useSecondaryNavTitle,
} from "@client/components/secondary-nav";
import { Button } from "@tubo/ui/components/ui/button";
import { useUiTranslation } from "@tubo/ui/i18n";
import { Badge } from "@tubo/ui/components/ui/badge";
import { Input } from "@tubo/ui/components/ui/input";
import { Label } from "@tubo/ui/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@tubo/ui/components/ui/dialog";
import {
  useSemanticNamespaceList,
  useSemanticNamespaceUpdate,
  useSemanticNamespaceDelete,
  getSemanticNamespaceListQueryKey,
} from "@tubo/api-models/hooks/semantic-namespace-api.hooks";
import type { SemanticNamespaceDO } from "@tubo/api-models/hooks/semantic-namespace-api.schemas";
import {
  NamespaceCreateDialog,
  namespaceErrorMessage,
  NS_CONTEXT_GLOBAL,
  isGlobalNamespace,
  isPackNamespace,
} from "@tubo/semantic-concepts";
import {
  SemanticAssetPackageExportActions,
  SemanticAssetPackagePanel,
} from "@client/components/semantic-platform/semantic-asset-package-panel";

export const Route = createFileRoute("/_app/semantic-platform/namespaces/")({
  component: NamespaceManagePage,
});

const PAGE_SIZE = 20;

/** 管理页行：真实领域 + 合成的「全局」行统一视图形态 */
interface NamespaceRow {
  id: string;
  code: string;
  name: string;
  desc?: string;
  conceptCount?: number;
  vocabularyCount?: number;
  modelCount?: number;
  quantityKindCount?: number;
  source: "system" | "user" | "pack";
}

const GLOBAL_ROW: NamespaceRow = {
  id: NS_CONTEXT_GLOBAL,
  code: "global",
  name: "",
  source: "system",
};

/** 挂载资产总数（四类语义资产引用计数之和；删除守卫依据） */
function usageTotal(ns: NamespaceRow): number {
  return (
    (ns.conceptCount ?? 0) +
    (ns.vocabularyCount ?? 0) +
    (ns.modelCount ?? 0) +
    (ns.quantityKindCount ?? 0)
  );
}

function NamespaceManagePage() {
  const { t } = useUiTranslation("semanticPlatform");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [createOpen, setCreateOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<SemanticNamespaceDO | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SemanticNamespaceDO | null>(null);
  const [packageOpen, setPackageOpen] = useState(false);

  const { data: namespaces = [], isLoading } = useSemanticNamespaceList({ query: {} });
  const errorMessages = {
    nameExists: t("namespaces.errors.nameExists"),
    nameEmpty: t("namespaces.errors.nameEmpty"),
    notFound: t("namespaces.errors.notFound"),
  };

  const rows = useMemo<NamespaceRow[]>(() => {
    const globalNamespace = namespaces.find((ns) => isGlobalNamespace(ns.code));
    const mapped = namespaces
      .filter((ns) => !isGlobalNamespace(ns.code))
      .map((ns): NamespaceRow => ({
        ...ns,
        source: isPackNamespace(ns.code) ? "pack" : "user",
      }));
    // 全局行恒在首位；行业包领域排在其后（随包装卸的托管容器）
    return [{ ...GLOBAL_ROW, ...globalNamespace, name: t("namespaces.globalName"), desc: t("namespaces.globalDescription"), source: "system" }, ...mapped];
  }, [namespaces, t]);

  const total = rows.length;
  const currentPage = Math.min(page, Math.max(1, Math.ceil(total / pageSize)));
  const pagedData = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: getSemanticNamespaceListQueryKey() });

  /** 进入领域：设置上下文并跳概念管理页 */
  const enterDomain = (row: NamespaceRow) => {
    navigate({
      to: "/semantic-platform/concepts",
      search: row.id === NS_CONTEXT_GLOBAL ? {} : { ns: row.id },
    });
  };

  const deleteMut = useSemanticNamespaceDelete({
    mutation: {
      onSuccess: () => {
        toast.success(t("namespaces.deleted"));
        invalidate();
        setDeleteTarget(null);
      },
      onError: (err) => toast.error(namespaceErrorMessage(err, t("namespaces.errors.deleteFailed"), errorMessages)),
    },
  });

  const sourceBadge = (source: NamespaceRow["source"]) => {
    if (source === "system") return <Badge variant="secondary">{t("namespaces.systemSource")}</Badge>;
    if (source === "pack")
      return (
        <Badge variant="outline" className="gap-1">
          <Lock className="size-3" /> {t("namespaces.industryPackage")}
        </Badge>
      );
    return <span className="text-xs text-muted-foreground">—</span>;
  };

  const columns: DataTableColumn<NamespaceRow>[] = [
    {
      id: "name",
      header: t("namespaces.name"),
      cell: (r) => (
        <span className="flex items-center gap-1.5 font-medium">
          <span className="min-w-0 truncate">{r.name}</span>
          {r.source === "pack" && <Lock className="size-3 shrink-0 text-muted-foreground" />}
        </span>
      ),
    },
    { id: "code", header: t("namespaces.code"), cell: (r) => <span className="font-mono text-sm">{r.code}</span> },
    {
      id: "usage",
      header: t("namespaces.usageHeader"),
      cell: (r) => (
        <span className="text-xs text-muted-foreground">
          {t("namespaces.usage", { concepts: r.conceptCount ?? "—", dictionaries: r.vocabularyCount ?? "—", models: r.modelCount ?? "—", quantities: r.quantityKindCount ?? "—" })}
        </span>
      ),
    },
    {
      id: "desc",
      header: t("namespaces.description"),
      cell: (r) => <span className="block max-w-64 truncate text-xs text-muted-foreground">{r.desc ?? "—"}</span>,
    },
    { id: "source", header: t("namespaces.source"), cell: (r) => sourceBadge(r.source) },
    {
      id: "actions",
      header: t("namespaces.actions"),
      cell: (r) => (
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => enterDomain(r)}>
            {t("namespaces.enter")}
          </Button>
          {r.source === "user" && (
            <>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("namespaces.rename")}
                title={t("namespaces.rename")}
                onClick={() => setRenameTarget(namespaces.find((ns) => ns.id === r.id) ?? null)}
              >
                <Pencil className="size-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label={t("namespaces.delete")}
                title={usageTotal(r) > 0 ? t("namespaces.deleteBlockedTitle", { count: usageTotal(r) }) : t("namespaces.delete")}
                className="text-destructive"
                disabled={usageTotal(r) > 0}
                onClick={() => setDeleteTarget(namespaces.find((ns) => ns.id === r.id) ?? null)}
              >
                <Trash2 className="size-3.5" />
              </Button>
            </>
          )}
          <SemanticAssetPackageExportActions namespaceId={r.id} name={r.code} />
        </div>
      ),
    },
  ];

  const headerTitle = useMemo(
    () => <span className="text-sm font-semibold">{t("namespaces.managementTitle")}</span>,
    [t],
  );
  useSecondaryNavTitle(headerTitle);

  const headerActions = useMemo(
    () => (
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="mr-1 size-4" /> {t("namespaces.create")}
        </Button>
        <Button variant="outline" size="sm" onClick={() => setPackageOpen(true)}>
          {t("namespaces.movePackages")}
        </Button>
      </div>
    ),
    [t],
  );
  useSecondaryNavActions(headerActions);

  return (
    <div className="h-full min-h-0 min-w-0">
      <DataTable
        data={pagedData}
        columns={columns}
        isLoading={isLoading}
        rowKey={(row) => row.id}
        page={currentPage}
        pageSize={pageSize}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        total={total}
        onPageChange={setPage}
        emptyState={
          <div className="py-6 text-center text-sm text-muted-foreground">
            <p>{t("namespaces.emptyTitle")}</p>
            <p className="mt-1 text-xs">{t("namespaces.emptyDescription")}</p>
          </div>
        }
      />

      <NamespaceCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
      <NamespaceRenameDialog target={renameTarget} onClose={() => setRenameTarget(null)} />
      <NamespaceDeleteDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={(id) => deleteMut.mutate({ id })}
        pending={deleteMut.isPending}
      />
      <SemanticAssetPackagePanel
        open={packageOpen}
        onOpenChange={setPackageOpen}
        onImported={invalidate}
        namespaces={namespaces.map((namespace) => ({ id: namespace.id, code: namespace.code, name: namespace.name }))}
      />
    </div>
  );
}

/** 重命名/说明更新（编码为归属标识，不可改） */
function NamespaceRenameDialog({
  target,
  onClose,
}: {
  target: SemanticNamespaceDO | null;
  onClose: () => void;
}) {
  const { t } = useUiTranslation("semanticPlatform");
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  // 弹窗复用：切换目标时同步初始值
  const [syncedId, setSyncedId] = useState<string | null>(null);
  if (target && syncedId !== target.id) {
    setSyncedId(target.id);
    setName(target.name);
    setDesc(target.desc ?? "");
  }

  const updateMut = useSemanticNamespaceUpdate({
    mutation: {
      onSuccess: () => {
        toast.success(t("namespaces.updated"));
        queryClient.invalidateQueries({ queryKey: getSemanticNamespaceListQueryKey() });
        onClose();
      },
      onError: (err) => toast.error(namespaceErrorMessage(err, t("namespaces.errors.updateFailed"), {
        nameExists: t("namespaces.errors.nameExists"),
        nameEmpty: t("namespaces.errors.nameEmpty"),
        notFound: t("namespaces.errors.notFound"),
      })),
    },
  });

  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("namespaces.renameDialog.title")}</DialogTitle>
          <DialogDescription>{t("namespaces.renameDialog.codeReadOnly", { code: target?.code })}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ns-rename-name">{t("namespaces.renameDialog.name")}</Label>
            <Input
              id="ns-rename-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("namespaces.renameDialog.namePlaceholder")}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ns-rename-desc">{t("namespaces.renameDialog.description")}</Label>
            <Input
              id="ns-rename-desc"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder={t("namespaces.renameDialog.descriptionPlaceholder")}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            {t("namespaces.renameDialog.cancel")}
          </Button>
          <Button
            size="sm"
            disabled={!name.trim() || updateMut.isPending}
            onClick={() =>
              target &&
              updateMut.mutate({ id: target.id, data: { name: name.trim(), desc: desc.trim() || undefined } })
            }
          >
            {t("namespaces.renameDialog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** 删除确认（仍有资产挂载时前端即拦截；后端守卫兜底） */
function NamespaceDeleteDialog({
  target,
  onClose,
  onConfirm,
  pending,
}: {
  target: SemanticNamespaceDO | null;
  onClose: () => void;
  onConfirm: (id: string) => void;
  pending: boolean;
}) {
  const { t } = useUiTranslation("semanticPlatform");
  const total = target
    ? (target.conceptCount ?? 0) + (target.vocabularyCount ?? 0) + (target.modelCount ?? 0) + (target.quantityKindCount ?? 0)
    : 0;
  return (
    <Dialog open={!!target} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("namespaces.deleteDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("namespaces.deleteDialog.description", { name: target?.name, code: target?.code })}
          </DialogDescription>
        </DialogHeader>
        {total > 0 && (
          <p className="rounded-md bg-amber-500/10 px-3 py-2 text-xs text-amber-600 dark:text-amber-400">
            {t("namespaces.deleteDialog.blocked", { count: total })}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>
            {t("namespaces.deleteDialog.cancel")}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={total > 0 || pending}
            onClick={() => target && onConfirm(target.id)}
          >
            {t("namespaces.deleteDialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
